import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ShieldCheck, RefreshCw, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import { authApi } from '@/api/auth';
import { useAuthStore } from '@/store/authStore';
import { storage } from '@/utils/storage';
import { cn } from '@/lib/utils';

const OTP_LENGTH = 4;
const RESEND_COUNTDOWN = 60;

export function VerifyOtpPage() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const { t } = useTranslation();
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(RESEND_COUNTDOWN);
  const [canResend, setCanResend] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const mobile = sessionStorage.getItem('login_mobile');

  useEffect(() => {
    if (!mobile) {
      navigate('/login');
      return;
    }

    // Focus first input
    inputRefs.current[0]?.focus();

    // Start countdown
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [mobile, navigate]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    // Move to next input
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto submit when complete
    if (newOtp.every((digit) => digit) && newOtp.join('').length === OTP_LENGTH) {
      handleVerify(newOtp.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, OTP_LENGTH);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    pastedData.split('').forEach((digit, index) => {
      if (index < OTP_LENGTH) {
        newOtp[index] = digit;
      }
    });
    setOtp(newOtp);

    if (pastedData.length === OTP_LENGTH) {
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (otpCode: string) => {
    if (!mobile) return;

    setIsLoading(true);
    try {
      const tokenResponse = await authApi.verifyOtp({
        Mobile: mobile,
        OTP: otpCode,
        ClientType: 'support',
      });

      // Store token first so getProfile can use it
      storage.setAccessToken(tokenResponse.tokenString);
      storage.setRefreshToken(tokenResponse.refreshToken);

      // Get user profile
      const profile = await authApi.getProfile();

      // Login to store (this also sets tokens but we need them before getProfile)
      login(tokenResponse.tokenString, tokenResponse.refreshToken, profile);

      // Clear session storage
      sessionStorage.removeItem('login_mobile');

      toast.success(t('auth.otp.loginSuccess', 'تم تسجيل الدخول بنجاح'));
      navigate('/');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('auth.otp.invalidCode', 'رمز التحقق غير صحيح'));
      setOtp(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!mobile || !canResend) return;

    try {
      await authApi.forceOtp(mobile, 'support');
      toast.success(t('auth.otp.resendSuccess', 'تم إعادة إرسال رمز التحقق'));
      setCountdown(RESEND_COUNTDOWN);
      setCanResend(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('common.error', 'حدث خطأ'));
    }
  };

  const isComplete = otp.every((digit) => digit);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="mx-auto w-12 h-12 rounded-md bg-primary flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-white" />
        </div>
        <div className="pt-1">
          <h1 className="text-lg font-semibold text-foreground">{t('auth.otp.title')}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t('auth.otp.subtitle')}
          </p>
          <p className="font-medium text-foreground mt-1" dir="ltr">
            {mobile}
          </p>
        </div>
      </div>

      {/* OTP Input */}
      <div className="flex justify-center gap-2.5" dir="ltr">
        {otp.map((digit, index) => (
          <div key={index} className="relative">
            <input
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              onFocus={() => setFocusedIndex(index)}
              onBlur={() => setFocusedIndex(null)}
              disabled={isLoading}
              className={cn(
                "w-11 h-12 text-center text-lg font-semibold rounded-md border transition-colors bg-background outline-none",
                focusedIndex === index ? "border-primary ring-1 ring-primary" : "border-input",
                digit ? "border-primary bg-primary/5" : "",
                isLoading && "opacity-50 cursor-not-allowed"
              )}
            />
            {/* Cursor animation when focused and empty */}
            {focusedIndex === index && !digit && (
              <span className="absolute top-1/2 start-1/2 -translate-x-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary animate-pulse" />
            )}
          </div>
        ))}
      </div>

      {/* Verify Button */}
      <Button
        onClick={() => handleVerify(otp.join(''))}
        loading={isLoading}
        disabled={!isComplete}
        className="w-full"
      >
        {!isLoading && t('auth.otp.verifyButton')}
      </Button>

      {/* Resend Timer */}
      <div className="text-center">
        {canResend ? (
          <button
            onClick={handleResend}
            className="inline-flex items-center gap-2 text-primary font-medium hover:underline underline-offset-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            {t('auth.otp.resend')}
          </button>
        ) : (
          <div className="space-y-0.5">
            <p className="text-muted-foreground text-sm">{t('auth.otp.didntReceive', 'لم تستلم الرمز؟')}</p>
            <p className="text-foreground text-sm">
              {t('auth.otp.resendIn')}{' '}
              <span className="inline-flex items-center justify-center min-w-[1.75rem] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium text-sm">
                {countdown}
              </span>
              {' '}{t('auth.otp.seconds')}
            </p>
          </div>
        )}
      </div>

      {/* Back Button */}
      <button
        onClick={() => navigate('/login')}
        className="w-full flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground text-sm transition-colors py-1.5"
      >
        <ArrowRight className="w-4 h-4" />
        {t('auth.otp.changeNumber')}
      </button>
    </div>
  );
}
